import React, { lazy, Suspense } from 'react';
import { FormApi, FormStateBasic } from 'informed';
import Yup from 'yup';

import { componentLoader } from '@nw/shared/utils';

import { AllFormFieldModelsType } from '../../types/FieldModelTypes';
import { CustomClassNamesInterface } from '../../types/MiscellaneousTypes';

// Lazy load RenderAllFormFields (Omega design system - default)
const LazyRenderAllFormFields = lazy(
    () =>
        componentLoader(
            () =>
                import(
          /* webpackChunkName: "RenderAllFormFields" */ '../RenderAllFormFields'
                ).then((module) => ({
                    default: module.RenderAllFormFields
                }))
        ) as Promise<{ default: React.ComponentType<any> }>
);

// Lazy load RenderAllShadcnFormFields (Shadcn design system)
const LazyRenderAllShadcnFormFields = lazy(
    () =>
        componentLoader(
            () =>
                import(
          /* webpackChunkName: "RenderAllShadcnFormFields" */ '../RenderAllShadcnFormFields'
                ).then((module) => ({
                    default: module.RenderAllShadcnFormFields
                }))
        ) as Promise<{ default: React.ComponentType<any> }>
);

interface InformedFieldsInfoInterface {
    formValues: FormStateBasic['values'];
    touched: FormStateBasic['touched'];
    errors: FormStateBasic['errors'];
    formApi: FormApi;
    stepFields: AllFormFieldModelsType[];
}

export interface FormRendererProps {
    informedFieldsInfo: InformedFieldsInfoInterface;
    registrationFormState: any;
    registrationFormSend: any;
    validationSchemaObject: Yup.AnyObjectSchema;
    onBlurEachField?: any;
    customClassNames?: CustomClassNamesInterface;
    shouldDisableAllFields?: boolean;
    handleFormReset?: () => void;
    designSystemType?: string;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
    informedFieldsInfo,
    registrationFormState,
    registrationFormSend,
    validationSchemaObject,
    onBlurEachField,
    customClassNames,
    shouldDisableAllFields,
    handleFormReset,
    designSystemType
}) => {
    // Determine which component to render based on designSystemType
    const shouldUseShadcnDesignSystem =
        designSystemType && designSystemType === 'SHADCN_DESIGN_SYSTEM';

    const LazyComponent = shouldUseShadcnDesignSystem
        ? LazyRenderAllShadcnFormFields
        : LazyRenderAllFormFields;

    return (
        <Suspense fallback={null}>
            <LazyComponent
                informedFieldsInfo={informedFieldsInfo}
                registrationFormState={registrationFormState}
                registrationFormSend={registrationFormSend}
                validationSchemaObject={validationSchemaObject}
                onBlurEachField={onBlurEachField}
                customClassNames={customClassNames}
                shouldDisableAllFields={shouldDisableAllFields}
                handleFormReset={handleFormReset}
            />
        </Suspense>
    );
};

